import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { useOrg } from "@/contexts/OrgContext";
import * as actions from "@/redux/actions";
import SntOrgMenuSelectorContainer from "@containers/SntOrgSelectorContainer/SntOrgMenuSelectorContainer";

import NavList from "./NavList/NavList";
import useHomeMenu from "./Menus/useHomeMenu";
import useSelectedOrgMenu from "./Menus/useSelectedOrgMenu";
import usePartnerMenu from "./Menus/usePartnerMenu";
import useSystemMenu from "./Menus/useSystemMenu";

import HomeIcon from "@images/menu/home.svg";
import CogIcon from "@images/menu/cog.svg";
import SensolusLogoIcon from "@images/sensolus/sensolus_logo_white.svg";
import { MenuTypeConst, type MenuRoute } from "./Menus/menuTypes";
import { getHashFromLandingPage } from "@/containers/LandingPageHelper/LandingPageHelper";
import type { RootState } from "@/types/RootState";
import type { LoginInfo } from "@/types/LoginInfo";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { hasPermission } from "@/hooks/usePermissions";
import { UserPermission } from "@/types/generated/UserPermission";

/**
 * Extended menu data with type and loading state
 */
interface MenuData {
  type: string;
  loading: boolean;
  routes: MenuRoute[];
  allPaths: Record<string, MenuRoute>;
  availableMenuPaths: Record<string, MenuRoute>;
  forceMenuPaths?: Record<string, MenuRoute>;
  defaultPath: { pathname: string; search: string } | string;
  isAdminMenuAvailable?: boolean;
}

/**
 * Organisation selector change data
 */
interface OrgSelectorData {
  value: number | null;
}

const SidebarBody = () => {
  const orgContext = useOrg();
  const { orgId, updateOrg } = orgContext ?? { orgId: null, updateOrg: (_newOrgId: number | null) => {} };
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const loginInfo = useSelector((state: RootState) => state.user) as LoginInfo;
  const { whiteLabel } = loginInfo;

  const homeMenu = useHomeMenu(loginInfo) as MenuData;
  const selectedOrgMenu = useSelectedOrgMenu(loginInfo, orgId ?? undefined) as MenuData;
  const partnerMenu = usePartnerMenu(loginInfo) as MenuData;
  const systemMenu = useSystemMenu(loginInfo) as MenuData;

  const [selectedMenu, setSelectedMenu] = useState<MenuData>(homeMenu);

  const [lastHomeMenu, setLastHomeMenu] = useState<{ pathname: string; search: string } | null>(null);
  const [lastAdminMenu, setLastAdminMenu] = useState<{ pathname: string; search: string } | null>(null);
  const [lastSelelectedOrgMenu, setLastSelectedOrgMenu] = useState<{ pathname: string; search: string } | null>(null);
  const [lastAllMenu, setLastAllMenu] = useState<{ pathname: string; search: string } | null>(null);

  const [showFooter, setShowFooter] = useState<boolean>(false);

  const findRouteInAllPaths = (allPaths: Record<string, MenuRoute>, path: string): MenuRoute | null => {
    // First try exact match
    if (allPaths[path]) {
      return allPaths[path];
    }
    // Then try prefix match for paths with dynamic segments (e.g., /site_map_view/514)
    for (const key in allPaths) {
      if (path.startsWith(key + "/")) {
        return allPaths[key];
      }
    }
    return null;
  };

  const getMenuByLocationPath = (path: string): MenuData => {
    let routeItem = findRouteInAllPaths(homeMenu.allPaths, path);
    if (routeItem) {
      return homeMenu;
    }

    routeItem = findRouteInAllPaths(selectedOrgMenu.allPaths, path);
    if (routeItem) {
      return selectedOrgMenu;
    }

    if (loginInfo?.organisation?.organisationType === "PARTNER") {
      routeItem = findRouteInAllPaths(partnerMenu.allPaths, path);
      if (routeItem) {
        return partnerMenu;
      }
    } else if (loginInfo?.organisation?.organisationType === "SYSTEM") {
      routeItem = findRouteInAllPaths(systemMenu.allPaths, path);
      if (routeItem) {
        return systemMenu;
      }
    }

    return homeMenu;
  };

  useEffect(() => {
    if (
      homeMenu.loading ||
      selectedOrgMenu.loading ||
      partnerMenu.loading ||
      systemMenu.loading
    ) {
      return;
    }

    const menu = getMenuByLocationPath(location.pathname);
    setSelectedMenu(menu);
    dispatch(actions.setMenuType(menu.type));

    // empty menu
    if (
      Object.keys(menu.availableMenuPaths).length === 0 &&
      location.pathname !== "/permission"
    ) {
      history.push("/permission");
      return;
    }
    // protected route
    const mainRoute = menu.allPaths[location.pathname];
    if (!mainRoute && location.pathname !== "/") return;
    if (
      location.pathname !== "/no_access" &&
      location.pathname !== "/permission" &&
      location.pathname !== "/"
    ) {
      if (
        (location.pathname.includes("/bi_dashboard/") &&
          !menu.availableMenuPaths["/all_dashboards"]) ||
        (!location.pathname.includes("/bi_dashboard/") &&
          (!mainRoute ||
            !(
              menu.availableMenuPaths[mainRoute.to ?? ""] ||
              menu.availableMenuPaths[location.pathname]
            )))
      ) {
        if (menu.forceMenuPaths && menu.forceMenuPaths[mainRoute.to ?? ""]) {
          const path = menu.defaultPath;
          history.push(`${typeof path === "string" ? path : path.pathname}?org=${orgId}`);
        } else {
          history.push("/no_access");
        }
        return;
      }
    }

    if (menu.type === MenuTypeConst.ADMIN) {
      const adminMatchRoute = selectedOrgMenu.allPaths[location.pathname];
      if (
        adminMatchRoute &&
        !adminMatchRoute.subPages?.find((p) => p === location.pathname) &&
        !orgId
      ) {
        // top level -> need org id for admin url, update to own org
        updateOrg(loginInfo.organisation?.id ?? null);
      }
      setLastAdminMenu(location);
      setLastSelectedOrgMenu(location);
    } else if (menu.type === MenuTypeConst.MAIN) {
      setLastHomeMenu(location);
    } else {
      setLastAdminMenu(location);
      setLastAllMenu(location);
    }

    if (location.pathname === "" || location.pathname === "/") {
      let landingPath = {
        pathname: getHashFromLandingPage(loginInfo),
        search: "",
      };
      if (!homeMenu.availableMenuPaths[landingPath.pathname]) {
        landingPath = typeof homeMenu.defaultPath === "string"
          ? { pathname: homeMenu.defaultPath, search: "" }
          : homeMenu.defaultPath;
      }
      history.push(landingPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, selectedOrgMenu, partnerMenu, systemMenu, homeMenu]);

  useEffect(() => {
    if (selectedOrgMenu.isAdminMenuAvailable) {
      setShowFooter(true);
      return;
    }

    let isShowFooter = false;
    if (
      hasPermission(UserPermission.SYSTEM_ADMIN_VIEW) &&
      systemMenu.isAdminMenuAvailable
    ) {
      isShowFooter = true;
    } else if (
      loginInfo?.organisation?.organisationType === "PARTNER" &&
      partnerMenu.isAdminMenuAvailable
    ) {
      isShowFooter = true;
    }
    setShowFooter(isShowFooter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgMenu, partnerMenu, systemMenu]);

  const onOrganisationChanged = (data: OrgSelectorData): void => {
    if (data.value) {
      if (orgId) {
        const selectedMatchRoute = selectedMenu.allPaths[location.pathname];
        // location is detail page, back to overview menu when orgId changed. If not change orgId
        if (
          selectedMatchRoute?.subPages?.find((p) => p === location.pathname)
        ) {
          history.push(`${selectedMatchRoute.to}?org=${data.value}`);
        } else {
          updateOrg(data.value);
        }
      } else {
        // ALL menu back to selected org Menu
        const path = lastSelelectedOrgMenu || (typeof selectedOrgMenu.defaultPath === "string"
          ? { pathname: selectedOrgMenu.defaultPath, search: "" }
          : selectedOrgMenu.defaultPath);
        history.push(`${path.pathname}?org=${data.value}`);
      }
    } else {
      let path: { pathname: string; search: string } | string = "";
      if (loginInfo?.organisation?.organisationType === "PARTNER") {
        path = lastAllMenu || partnerMenu.defaultPath;
      } else {
        path = lastAllMenu || systemMenu.defaultPath;
      }
      // Strip the org parameter when switching to "All" mode
      if (typeof path === "object" && path.search) {
        const params = new URLSearchParams(path.search);
        params.delete("org");
        const newSearch = params.toString();
        path = { pathname: path.pathname, search: newSearch ? `?${newSearch}` : "" };
      }
      history.push(path);
    }
  };

  const onAdminButtonClicked = (): void => {
    if (lastAdminMenu) {
      history.push(lastAdminMenu);
    } else {
      const organisationType = loginInfo?.organisation?.organisationType;
      if (organisationType === "NORMAL") {
        history.push(selectedOrgMenu.defaultPath);
      } else if (organisationType === "PARTNER") {
        history.push(partnerMenu.defaultPath);
      } else {
        history.push(systemMenu.defaultPath);
      }
    }
  };

  const onHomeMenuClicked = (): void => {
    const path = lastHomeMenu || (typeof homeMenu.defaultPath === "string"
      ? { pathname: homeMenu.defaultPath, search: "" }
      : homeMenu.defaultPath);
    setSelectedMenu(homeMenu);
    history.push(path);
  };

  return (
    <>
      {selectedMenu.type !== homeMenu.type &&
        (loginInfo?.organisation?.organisationType === "SYSTEM" ||
          loginInfo?.organisation?.organisationType === "PARTNER") && (
          <div
            style={{
              zIndex: 2005,
              backgroundColor: "var(--admin-sidebar-bg-color)",
            }}
          >
            <div style={{ padding: 10, width: "100%" }}>
              <SntOrgMenuSelectorContainer
                onChange={onOrganisationChanged}
                value={orgId === null ? 0 : orgId}
              />
            </div>
          </div>
        )}
      <div className="sidebar-middle">
        <NavList
          orgId={orgId !== null ? String(orgId) : undefined}
          location={location}
          routes={selectedMenu.routes}
          collapsible={selectedMenu.type === homeMenu.type}
          menuType={selectedMenu.type}
        />
      </div>
      {showFooter || !whiteLabel ? (
        <div
          className={
            "sidebar-footer " +
            (selectedMenu.type === MenuTypeConst.MAIN ? "home-menu" : "admin-menu")
          }
        >
          {showFooter && (
            <div className="nav-icon-bar">
              <div className="nav-icon-wrapper">
                <div
                  className={
                    selectedMenu.type === homeMenu.type
                      ? "nav-icon active"
                      : "nav-icon"
                  }
                  onClick={onHomeMenuClicked}
                >
                  <img alt="home" src={HomeIcon} />
                  <div>{translate(LanguageKey.home_key)}</div>
                </div>
              </div>
              <div className="nav-icon-wrapper">
                <div
                  className={
                    selectedMenu.type !== homeMenu.type
                      ? "nav-icon active"
                      : "nav-icon"
                  }
                  onClick={onAdminButtonClicked}
                >
                  <img alt="admin" src={CogIcon} />
                  <div>{translate(LanguageKey.sidebar_admin_key)}</div>
                </div>
              </div>
            </div>
          )}
          {!whiteLabel && (
            <div className="powered-by">
              <div>{translate(LanguageKey.powered_by_key)}</div>
              <img src={SensolusLogoIcon} alt="sensolus" />
            </div>
          )}
        </div>
      ) : (
        ""
      )}
    </>
  );
};

export default SidebarBody;
